import React, { useEffect, useState } from 'react'
import { Alert, Button, IconButton, MenuItem, Select } from '@mui/material';
import moment from 'moment';
import dayjs from 'dayjs';

import { IAddedShipmentDocument, IShipmentDocument } from '../../types/shipmentPacket';
import { AppConfig } from '../../config/appConfig';

import { DocumentService } from '../../services/documentService';
import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';
import Spinner from '../spinner';
import Json from '../json';

export interface IShipmentPacketTableProps {
	data: IShipmentDocument[] | undefined;
	isLoading?: boolean;
	documentTypeFilter?: string;
	bolFilter?: string;
	customerFilter?: string;
	vendorFilter?: string;
	startDate?: dayjs.Dayjs;
	endDate?: dayjs.Dayjs;
	documentContentsChanged: (contents: IAddedShipmentDocument[]) => void;
}

const ShipmentPacketTable: React.FunctionComponent<IShipmentPacketTableProps> = ({ data, documentTypeFilter, isLoading, bolFilter, customerFilter, vendorFilter, startDate, endDate, documentContentsChanged }) => {
	const [addedDocuments, setAddedDocuments] = useState<IAddedShipmentDocument[]>([]);
	const [searchResults, setSearchResults] = useState<IShipmentDocument[]>([]);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(AppConfig.defaultPageSize);
	const [isInitialLoading, setIsInitialLoading] = useState(true);
	const [searchErrorMessage, setSearchErrorMessage] = useState<string | undefined>();
	const [generateErrorMessage, setGenerateErrorMessage] = useState<string | undefined>();
	const [redrawTrigger, setRedrawTrigger] = useState(0); // Used to force re-render of combined document table rows by altering their key

	const [sortField, setSortField] = useState(AppConfig.shipmentPacketListDefaultSortField);
	const [sortAscending, setSortAscending] = useState(true);

	useEffect(() => {
		if (data) {
			setIsInitialLoading(false);
		}
	}, [data]);

	useEffect(() => {
		if (isLoading) {
			setIsInitialLoading(false);
		}
	}, [isLoading])

	useEffect(() => {
		if (data) {
			let newData = data.filter((item) => {
				if (documentTypeFilter && documentTypeFilter != "All" && item.documentTypeId != documentTypeFilter) {
					return false;
				}
				if (bolFilter && bolFilter.trim() != "" && !item.bol.toLowerCase().includes(bolFilter.trim().toLowerCase())) {
					return false;
				}
				if (customerFilter && customerFilter != "All" && item.customerId != customerFilter) {
					return false;
				}
				if (vendorFilter && vendorFilter != "All" && item.vendorId != vendorFilter) {
					return false;
				}
				return true;
			}).slice();

			// Start sort as descending if the field name starts with "-"
			let _sortField = sortField;
			if (sortField.startsWith("-")) {
				setSortAscending(false);
				_sortField = sortField.substring(1);
				setSortField(_sortField);
			}
			if (sortField) {
				switch (sortField) {
					case "documenttype": {
						newData.sort((a, b) => UtilityService.compareStrings(a.documentTypeName, b.documentTypeName));
						break;
					}
					case "bol": {
						newData.sort((a, b) => UtilityService.compareStrings(a.bol, b.bol));
						break;
					}
					case "customername": {
						newData.sort((a, b) => UtilityService.compareStrings(a.customerName, b.customerName));
						break;
					}
					case "vendorname": {
						newData.sort((a, b) => UtilityService.compareStrings(a.vendorName, b.vendorName));
						break;
					}
					case "shipmentdate": {
						newData.sort((a, b) => UtilityService.compareNumbers(a.shipmentDate?.getTime(), b.shipmentDate?.getTime()));
						break;
					}
				}
			}
			if (!sortAscending) newData.reverse();

			setSearchResults(newData);
		}
	}, [data, documentTypeFilter, bolFilter, customerFilter, vendorFilter, startDate, endDate, sortField, sortAscending])

	useEffect(() => {
		setPage(0);
	}, [documentTypeFilter, customerFilter, vendorFilter])

	useEffect(() => {
		documentContentsChanged?.([ ...addedDocuments ]);
		checkUnverifiedExistsInAzure();
	}, [addedDocuments])

	useEffect(() => {
		documentContentsChanged?.([ ...addedDocuments ]);
	}, [redrawTrigger])

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	const handleAddToCombinedDocument = (documents: IShipmentDocument[]) => {
		const newDocuments = documents
			.filter(doc => !addedDocuments.some(ad => ad.documentId === doc.documentId)) // Ignore documents that have already been added
			.map((doc, index) => ({ ...doc, ordinal: addedDocuments.length + index + 1, isVerifying: true } as IAddedShipmentDocument)); // Add ordinal and set verifying

		setAddedDocuments([
			...addedDocuments,
			...newDocuments
		]);
	}

	const handleRemoveFromCombinedDocument = (documentId: number) => {
		const updatedDocuments = addedDocuments.filter(doc => doc.documentId !== documentId)
			.map((doc, index) => ({ ...doc, ordinal: index + 1 })); // Re-assign ordinals

		setAddedDocuments(updatedDocuments);
	}

	const handlePageBack = () => {
		setPage(page - 1);
	}

	const handlePageForward = () => {
		setPage(page + 1);
	}

	const checkUnverifiedExistsInAzure = () => {
		const documentIds = addedDocuments?.filter(doc => doc.isVerifying)?.map(doc => doc.documentId) ?? [];
		if (documentIds?.length < 1) return;

		DocumentService.checkExistsInAzure(documentIds)
			.then((results) => {
				results.forEach((status, index) => {
					const addedDoc = addedDocuments.find(doc => doc.documentId === status.documentId);
					if (addedDoc) {
						addedDoc.isAvailable = status.isContentsAvailable;
						addedDoc.isVerifying = false;
					}
				});
			})
			.catch((error) => {
				console.error("Error checking if documents exist in Azure:", error);
			})
			.finally(() => {
				setRedrawTrigger(prev => prev + 1); // Force re-render of combined table rows
			});
	}

	const handleOrdinalChannge = (source: number, target: number) => {
		setGenerateErrorMessage(undefined);
		const updatedDocuments = [...addedDocuments];
		const sourceDoc = updatedDocuments.find(doc => doc.ordinal == source);
		const targetDoc = updatedDocuments.find(doc => doc.ordinal == target);

		if (!targetDoc) {
			setGenerateErrorMessage(`Error updating document order: Invalid ordinal: ${target}`);
			return;
		}
		targetDoc.ordinal += target > source ? -0.5 : 0.5;

		if (!sourceDoc) {
			setGenerateErrorMessage(`Error updating document order: Invalid ordinal: ${source}`);
			return;
		}
		sourceDoc.ordinal = target;

		updatedDocuments.sort((a, b) => UtilityService.compareNumbers(a.ordinal, b.ordinal));
		updatedDocuments.forEach((doc, index) => {
			doc.ordinal = index + 1;
		});
		setAddedDocuments(updatedDocuments);
	}

	return (
		<>
			<section className="hwi-entity-search-results-table">
				{searchErrorMessage &&
					<Alert className="alert-error"
						severity="error"
						action={<IconButton onClick={() => { setSearchErrorMessage(undefined); }} size='small'>X</IconButton>}>
						{searchErrorMessage}
					</Alert>
				}
				<div className="headers">
					<div className="document-type-col">
						<p onClick={() => handleHeaderClick("documenttype")}>
							Document Type
							{" "}
							{(sortField == "documenttype" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
							{(sortField == "documenttype" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
						</p>
					</div>
					<div className="bol-col">
						<p onClick={() => handleHeaderClick("bol")}>
							BOL
							{" "}
							{(sortField == "bol" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
							{(sortField == "bol" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
						</p>
					</div>
					<div className="customer-col">
						<p onClick={() => handleHeaderClick("customer")}>
							Customer
							{" "}
							{(sortField == "customer" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
							{(sortField == "customer" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
						</p>
					</div>
					<div className="vendor-col">
						<p onClick={() => handleHeaderClick("vendor")}>
							Vendor
							{" "}
							{(sortField == "vendor" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
							{(sortField == "vendor" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
						</p>
					</div>
					<div className="shipment-date-col">
						<p onClick={() => handleHeaderClick("shipmentdate")}>
							Shipment Date
							{" "}
							{(sortField == "shipmentdate" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
							{(sortField == "shipmentdate" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
						</p>
					</div>
					<div className="controls-col">
						{searchResults?.length > 0 &&
							<Button
								className='clear'
								variant="contained"
								color="primary"
								size="small"
								onClick={() => {
									handleAddToCombinedDocument(searchResults.filter((_, index) => index >= (page * pageSize) && index <= ((page + 1) * pageSize) - 1));
								}}
							>Add All</Button>
						}
					</div>
				</div>
				<Spinner dependency={isLoading} text="Searching shipment documents...">
					<div className="data">
						{searchResults && searchResults?.length > 0 ?
							(
								searchResults?.slice(page * pageSize, (page + 1) * pageSize).map((item, index) => (
									<div className={`row${addedDocuments.find(ad => searchResults.some(sr => sr.documentId == ad.documentId && sr.documentId == item.documentId)) ? " document-added" : ""}`} key={item.documentId}>
										<div className="document-type-col"><p>{item.documentTypeName}</p></div>
										<div className="bol-col"><p>{item.bol}</p></div>
										<div className="customer-col"><p>{item.customerName}</p></div>
										<div className="vendor-col"><p>{item.vendorName}</p></div>
										<div className="shipment-date-col"><p>{moment(item.shipmentDate).format("MM/DD/yyyy")}</p></div>
										<div className="controls-col">
											{addedDocuments.find(ad => searchResults.some(sr => sr.documentId == ad.documentId && sr.documentId == item.documentId)) ? (
												<div className='added'>Added</div>
											) : (
												<Button variant="outlined" color="primary" size="small" onClick={() => { handleAddToCombinedDocument([item]); }}>Add</Button>
											)}
										</div>
									</div>
								))
							) : (<div className="no-results">{isInitialLoading ? "Perform a search to begin." : "0 results"}</div>)}
					</div>
					{searchResults?.length > 0 &&
						<div className="pager">
							<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={page < 1} onClick={handlePageBack}>Previous</Button></div>
							<div className="pager-text">{`${(page * pageSize) + 1} to ${((page + 1) * pageSize) <= searchResults.length ? ((page + 1) * pageSize) : searchResults.length} of ${searchResults.length}`}</div>
							<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={((page + 1) * pageSize) >= searchResults.length} onClick={handlePageForward}>Next</Button></div>
						</div>
					}
				</Spinner>
				{/* <Json data={entityId} title="Entity:"></Json> */}
				{/* <Json data={entityTypeId} title="Entity Type:"></Json> */}
			</section>
			<section className="hwi-entity-search-results-table">
				<div className="title">Combined Document Contents</div>
				<div className="alert-container">
					{generateErrorMessage &&
						<Alert className="alert-error"
							severity="error"
							action={<IconButton onClick={() => { setGenerateErrorMessage(undefined); }} size='small'>X</IconButton>}>
							{generateErrorMessage}
						</Alert>
					}
					{addedDocuments?.some(doc => doc.isAvailable == false) &&
						<Alert className="alert-error" severity="error">
							One or more documents below (highlighted in red) are currently unavailable. Please remove them from the list, or contact an administrator for assistance.
						</Alert>
					}
					{addedDocuments.length == 1 &&
						<Alert className="alert-warning" severity="warning">
							At least <strong>2</strong> documents must be added to proceed.
						</Alert>
					}					
				</div>
				<div className="headers added-documents">
					<div className="ordinal-col"><p>Order</p></div>
					<div className="document-type-col"><p>Document Type</p></div>
					<div className="bol-col"><p>BOL</p></div>
					<div className="customer-col"><p>Customer</p></div>
					<div className="vendor-col"><p>Vendor</p></div>
					<div className="shipment-date-col"><p>Shipment Date</p></div>
					<div className="controls-col">
						{addedDocuments?.length > 0 &&
							<Button className='clear' variant="contained" color="error" size="small" onClick={() => { setAddedDocuments([]) }}>Clear</Button>
						}
					</div>
				</div>
				<div className="data added-documents">
					{addedDocuments?.length > 0 ?
						(
							addedDocuments?.map((item, index) => (
								<div className={`row${item.isAvailable == false ? " error" : ""}`} key={item.documentId.toString() + redrawTrigger.toString()}>
									<div className="ordinal-col">
										<Select
											value={item.ordinal}
											size="small"
											onChange={(event) => handleOrdinalChannge(item.ordinal, +event.target.value)}
										>
											{addedDocuments.map((doc, docIndex) => (
												<MenuItem value={docIndex + 1} key={docIndex}>{docIndex + 1}</MenuItem>
											))}
										</Select>
									</div>
									<div className="document-type-col"><p>{item.documentTypeName}</p></div>
									<div className="bol-col"><p>{item.bol}</p></div>
									<div className="customer-col"><p>{item.customerName}</p></div>
									<div className="vendor-col"><p>{item.vendorName}</p></div>
									<div className="shipment-date-col"><p>{moment(item.shipmentDate).format("MM/DD/yyyy")}</p></div>
									<div className="controls-container">
										<div className="controls-col">
											<Button variant="outlined" color="error" size="small" onClick={() => { handleRemoveFromCombinedDocument(item.documentId); }}>Remove</Button>
										</div>
										<div className="invalid-document-id">
											{!item.isVerifying && !item.isAvailable && <span>ID: {item.documentId}</span>}
										</div>
									</div>
								</div>
							))
						) : (<div className="no-results">No shipping documents have been added to the combined document.</div>)}
				</div>
				{/* <Json data={addedDocuments} title="Combined Document Contents:"></Json> */}
			</section>
		</>
	)
}

export default ShipmentPacketTable;